import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useTasks } from '../api/queries.ts';
import { useOrgCtx } from '../app/org-context.tsx';
import { PageHeader } from '../features/shell/Chrome.tsx';
import { DoneList, TaskGroupList } from '../features/tasks/TaskList.tsx';
import { TaskBoard } from '../features/tasks/TaskBoard.tsx';
import { EmptyState, PageLoader, Segmented } from '../ui/primitives.tsx';

type View = 'list' | 'board';

function readView(): View {
  try { return localStorage.getItem('unitly-task-view') === 'board' ? 'board' : 'list'; } catch { return 'list'; }
}

/** Task lists, used by "Le mie task" and by each workspace. */
export function TaskExplorer({ workspaceId, defaultScope = 'mine' }: { workspaceId?: string; defaultScope?: 'mine' | 'all' }) {
  const { orgId, openQuickAdd } = useOrgCtx();
  const [scope, setScope] = useState<'mine' | 'all'>(defaultScope);
  const [view, setViewState] = useState<View>(readView);
  const setView = (v: View) => { setViewState(v); try { localStorage.setItem('unitly-task-view', v); } catch { /* ignore */ } };
  const open = useTasks(orgId, { scope, status: view === 'board' ? 'all' : 'open', workspaceId });
  const done = useTasks(orgId, { scope, status: 'done', workspaceId, limit: 50 });

  return (
    <>
      <div className="toolbar">
        <Segmented label="Di chi" value={scope} onChange={setScope} options={[{ value: 'mine', label: 'Mie' }, { value: 'all', label: 'Tutte' }]} />
        <span className="spacer" />
        <Segmented label="Vista" value={view} onChange={setView} options={[{ value: 'list', label: 'Lista' }, { value: 'board', label: 'Board' }]} />
      </div>
      {open.isLoading && <PageLoader />}
      {open.data && open.data.length === 0 && view === 'list' && (
        <div className="card">
          <EmptyState title={scope === 'mine' ? 'Nessuna task aperta per te' : 'Nessuna task aperta'}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => openQuickAdd({ workspaceId })}><Plus size={15} /> Nuova task</button>
          </EmptyState>
        </div>
      )}
      {open.data && view === 'list' && <TaskGroupList tasks={open.data} showWorkspace={!workspaceId} workspaceId={workspaceId} />}
      {open.data && view === 'board' && <TaskBoard tasks={open.data} showWorkspace={!workspaceId} />}
      {view === 'list' && done.data && <DoneList tasks={done.data} showWorkspace={!workspaceId} />}
    </>
  );
}

export function TasksPage() {
  const { openQuickAdd } = useOrgCtx();
  return (
    <div className="page wide">
      <PageHeader
        title="Task"
        actions={<button type="button" className="btn btn-primary btn-sm" onClick={() => openQuickAdd()}><Plus size={16} /> Nuova</button>}
        mobileActions={<button type="button" className="icon-btn" aria-label="Nuova task" onClick={() => openQuickAdd()}><Plus size={20} /></button>}
      />
      <TaskExplorer />
    </div>
  );
}
